import axios from 'axios';
import { Campaign, Engagement, EngagementResponse, User, UserCampaign } from '@wave-engagement/wave-react';

// Create your axios with credentials object
const BASE_URL = process.env.REACT_APP_SERVER_URL ?? 'http://localhost:8000/api/user';
const axiosWithCredentials = axios.create({
  withCredentials: true,
  baseURL: BASE_URL
});

// Intercept the success and errors, if needed
axiosWithCredentials.interceptors?.response?.use(response => {
  return Promise.resolve(response?.data != null ? (response.data === '' ? null : response.data) : response);
}, error => {
  // if (error?.response?.status === 401) { // TODO
  //   clearCookies(store.dispatch);
  // }
  return Promise.reject(error?.response?.data?.message || error?.response?.data || error);
});

export function checkCampaign(code) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get(`/auth/campaign/${code}`).then(res => {
      resolve(Campaign.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });

}

export function registerAccount(userObj) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/auth/register', userObj).then(res => {
      resolve(User.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });
}

export function login({ email, password }) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/auth/login', { email, password }).then(res => {
      resolve(User.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });
}

export function forgotPasswordRequest({ email }) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/auth/forgot-password/request', { email }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function forgotPasswordConfirm({ verificationCode, password }) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/auth/forgot-password/reset', { verificationCode, password }).then(res => {
      resolve(User.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });
}

/* ----- LOGGED-IN ROUTES ----- */

export function getUser() {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get('/').then(res => {
      resolve(User.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getAccount() {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get('/account').then(res => {
      resolve(User.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateProfile({ firstName, lastName, phoneNumber, screenName }) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/account', { firstName, lastName, phoneNumber, screenName }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function logout() {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/auth/logout').then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function verifyEmail(verificationCode) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/auth/verify', { verificationCode }).then(res => {
      resolve(User.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });
}

export function resendVerify() {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/verify/resend').then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });

}

export function changePassword(password) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/account/change-password', { password }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateNotifications(notifications) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/account/notifications', notifications).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function getCampaign() {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get('/campaign').then(res => {
      resolve(UserCampaign.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listLeaderboard() {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get('/campaign/leaderboard').then(res => {
      resolve(UserCampaign.thawList(res));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listEngagements() {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get('/engagement').then(res => {
      resolve(Engagement.thawList(res));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getEngagement(id) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get(`/engagement/${id}`).then(res => {
      resolve(Engagement.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });
}

export function submitEngagementResponse(engagementId, questionResponses) {
  return new Promise(function(resolve, reject) {
    const data = new FormData();
    
    // Add any images to form data
    const photos = [];
    for (const questionResponse of questionResponses) {
      if (questionResponse.type === 'PHOTO_UPLOAD' && questionResponse.value != null) {
        photos.push(questionResponse.value);
        questionResponse.value = photos.length - 1;
      }
    }
    photos.forEach(photo => {
      data.append('photos', {
        uri: photo,
        type: 'image/png',
        name: 'image.png'
      });
    });

    data.append('questionResponses', JSON.stringify(questionResponses));

    axiosWithCredentials.post(`/engagement/${engagementId}`, data).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function listEngagementResponses(engagementId) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get(`/engagement/${engagementId}/response`).then(res => {
      resolve(EngagementResponse.thawList(res));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listImageUrls() {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get('/account/photos').then(res => {
      resolve(res);
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateDemographics(demographics) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/account/demographics', demographics).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateCampaignCode(campaignCode) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post(`/account/campaign/${campaignCode}`).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}